import { debugOverrides } from './debug.overrides.ts'
import { DEFAULT_CHAIN } from './chains.ts'

/** Shown in WalletConnect metadata and branding. Not driven by env. */
export const APP_DISPLAY_NAME = 'Bits Legacy' as const

/**
 * Parse the chain id from `VITE_CHAIN_ID`. Returns the default chain id when
 * the env var is missing or unparseable so the app still boots.
 */
function parseChainId(): number {
  const raw = (import.meta.env.VITE_CHAIN_ID as string | undefined)?.trim()
  if (!raw) return DEFAULT_CHAIN.id
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_CHAIN.id
}

/**
 * Optional public site origin (no trailing slash), e.g. `https://app.bitslegacy.com`.
 * Use when links must not rely on `window.location` (rare). Otherwise the app uses the
 * current browser origin.
 */
export const env = {
  publicOriginOverride:
    (import.meta.env.VITE_PUBLIC_ORIGIN as string | undefined)?.trim() ?? '',
  /** Vite mode: `development` | `production` | custom */
  appEnv: import.meta.env.MODE,
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  /** Dev-only: debug panel, verbose logs, test runner. Uses Vite `import.meta.env.DEV`. */
  enableDebug: import.meta.env.DEV,
  /**
   * Reown (WalletConnect) AppKit product analytics — not Google Analytics.
   * https://docs.reown.com — helps improve SDK usage insights when enabled.
   */
  enableAnalytics: true as const,
  reownProjectId: import.meta.env.VITE_REOWN_PROJECT_ID as string,
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL as string,
  supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
  contractAddress: import.meta.env.VITE_CONTRACT_ADDRESS as string,
  /** Numeric EVM chain id where the contract is deployed (source of truth). */
  chainId: parseChainId(),
  /** RPC URL for {@link env.chainId}. Empty string means: use viem's default public RPC. */
  rpcUrl: (import.meta.env.VITE_RPC_URL as string | undefined)?.trim() || '',
  litProxyUrl: (import.meta.env.VITE_LIT_PROXY_URL as string) || '',
  pinataGateway: import.meta.env.VITE_PINATA_GATEWAY as string,
  notificationApiBase:
    (import.meta.env.VITE_NOTIFICATION_API_BASE as string) || '',
  vapidPublicKey: (import.meta.env.VITE_VAPID_PUBLIC_KEY as string) || '',
} as const

/**
 * Canonical public origin for WalletConnect metadata, transactional email links, etc.
 *
 * When `import.meta.env.DEV`, `debug.overrides.ts` → `publicAppUrl` wins first,
 * then `VITE_PUBLIC_ORIGIN`, then browser origin, then default.
 */
export function getPublicAppOrigin(): string {
  if (env.enableDebug) {
    const debugUrl = debugOverrides.publicAppUrl?.trim()
    if (debugUrl) return debugUrl.replace(/\/$/, '')
  }

  const fromEnv = env.publicOriginOverride
  if (fromEnv) return fromEnv.replace(/\/$/, '')
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  return 'https://bitslegacy.com'
}
